import { useState, useContext, createContext } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const GlobalSearchContext = createContext();

export const GlobalSearchProvider = ({ children }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchableRoutes = [
    { name: 'Home', to: '/', category: 'Main' },
    { name: 'About Us', to: '/introduction/sploca', category: 'About' },
    { name: 'Organization Chart', to: '/org-chart', category: 'About' },
    { name: 'Government Orders', to: '/ca/authority/govtorders', category: 'About' },
    { name: 'Assigned Financial Establishments', to: '/ca/authority/assingnedfe', category: 'About' },
    { name: 'Calendar Events', to: '/about/calenderEvent', category: 'About' },
    { name: 'Geo Property', to: '/properties', category: 'Data' },
    { name: 'Contact Department', to: '/contact/departmentcontacts', category: 'Contact' },
    { name: 'Grievance & Suggestion', to: '/claim-mgmt', category: 'Contact' },
    { name: 'Employee Directory', to: '/legal/law/legal-team', category: 'Employee Directory' },
    { name: 'Special Court Cases', to: '/legal/court-cases/daily-report', category: 'Legal' },
    { name: 'High Court Cases', to: '/daily-high-court-cases', category: 'Legal' },
    { name: 'Supreme Court Cases', to: '/daily-supreme-court-cases', category: 'Legal' },
    { name: 'KPID Act', to: '/legal/law/kpid-act', category: 'Legal' },
    { name: 'BUDS Act', to: '/legal/law/buds-act', category: 'Legal' },
    { name: 'Other State PIDFE', to: '/other-state-pidfe', category: 'Legal' },
    { name: 'Other Relevant Acts', to: '/other-relevent-acts', category: 'Legal' },
    { name: 'Comparison of Acts', to: '/comparisn-of-acts', category: 'Legal' },
    { name: 'Special Court Orders', to: '/legal/court-cases/key-judgements', category: 'Legal' },
    { name: 'High Court Orders', to: '/high-court-orders', category: 'Legal' },
    { name: 'Supreme Court Orders', to: '/supreme-court-orders', category: 'Legal' },
    { name: 'Other State Court Orders', to: '/other-state-court-orders', category: 'Legal' },
    { name: 'SOP', to: '/sop', category: 'Legal' },
    { name: 'Legal Opinion', to: '/legal-opinion', category: 'Legal' },
    { name: 'Legal Research', to: '/legal-research', category: 'Legal' },
    { name: 'FAQs', to: '/faq', category: 'Legal' },
    { name: 'Legal Queries', to: '/legal-blogs', category: 'Legal' },
    { name: 'Scam News', to: '/scam-news', category: 'Legal' },
    { name: 'Circulars & Notifications', to: '/notification/circulars', category: 'Notifications' },
    { name: 'Office Orders', to: '/notification/sploca-circular/office-orders', category: 'Notifications' },
    { name: 'Reviews & Meetings', to: '/notification/rc-ex-office/reviews-meetings', category: 'Notifications' },
    { name: 'Tenders', to: '/notification/public-notices/tender', category: 'Notifications' },
    { name: 'Auctions', to: '/notification/public-notices/auction', category: 'Notifications' },
    { name: 'Advertisements', to: '/notification/public-notices/adv', category: 'Notifications' },
    { name: 'Summons', to: '/notification/public-notices/summons', category: 'Notifications' },
    { name: 'Warrants', to: '/notification/public-notices/warrants', category: 'Notifications' },
    { name: 'Notices', to: '/notification/public-notices/notices', category: 'Notifications' },
    { name: 'Depositor Information', to: '/notification/public-notices/ice/info-to-depositors', category: 'Notifications' },
    { name: 'ICE Notices', to: '/notification/public-notices/ice/notices', category: 'Notifications' },
    { name: 'Manuals', to: '/notification/public-notices/ice/manuals-videos', category: 'Notifications' },
    { name: 'Grievance Suggestions', to: '/notification/public-notices/grievance-suggestions', category: 'Notifications' },
    { name: 'Revenue Dept Karnataka', to: 'https://www.rdservices.karnataka.gov.in/', category: 'Quick Links', external: true },
    { name: 'Karnataka Police', to: 'https://ksp.karnataka.gov.in/english', category: 'Quick Links', external: true },
    { name: 'IEPF Authority', to: 'https://www.iepf.gov.in/', category: 'Quick Links', external: true },
    { name: 'SFIO', to: 'https://sfio.gov.in/en/', category: 'Quick Links', external: true },
    { name: 'Regional Commissioner', to: 'https://rcbengaluru.kar.nic.in/', category: 'Quick Links', external: true },
    { name: 'Dept Parliamentary', to: 'https://dpal.karnataka.gov.in/en', category: 'Quick Links', external: true },
    { name: 'High Court', to: 'https://judiciary.karnataka.gov.in/', category: 'Quick Links', external: true },
    { name: 'Supreme Court', to: 'https://www.sci.gov.in/', category: 'Quick Links', external: true },
    { name: 'RTI Login', to: 'https://rtionline.karnataka.gov.in/RTIMIS/login/', category: 'RTI', external: true },
    { name: 'RTI Manual Statistics', to: 'https://ceg.karnataka.gov.in/assets/front/pdf/rti%20manual/RTI%20Manual%20English.pdf', category: 'RTI', external: true },
    { name: 'RTI Statistics', to: 'https://ceg.karnataka.gov.in/assets/front/pdf/rti%20manual/RTI%20Manual%20English.pdf', category: 'RTI', external: true },
    { name: 'Login', to: '/accounts', category: 'Account' },
    { name: 'Officers Login', to: '/officers-login', category: 'Account' },
    { name: 'Profile', to: '/profile', category: 'Account' },
    { name: 'Claim Management', to: '/claim-mgmt', category: 'Claims' },
    { name: 'My Claims', to: '/claims', category: 'Claims' }
  ];

  // COMPLETE content search covering ALL APIs from your components
  const searchContent = async (query) => {
    const lowerQuery = query.toLowerCase().trim();
    const contentResults = [];
    const searchPromises = [];

    // 1. Government Orders
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/govt-orders/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/ca/authority/govtorders',
          category: 'Government Orders',
          description: `Government Order - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // // 2. Financial Establishments
    // searchPromises.push(
    //   axios.get(`${API_BASE_URL}/home-notification/fe/`)
    //     .then(response => response.data.filter(item => 
    //       (item.name_of_fe && item.name_of_fe.toLowerCase().includes(lowerQuery))
    //     ).map(item => ({
    //       type: 'content',
    //       title: item.name_of_fe,
    //       path: '/financial-establishments',
    //       category: 'Financial Establishments',
    //       description: 'Financial Establishment Details',
    //       link: item.link,
    //       date: '',
    //       relevance: calculateRelevance(item, lowerQuery, ['name_of_fe'])
    //     })))
    //     .catch(() => [])
    // );

    // 3. Assigned Financial Establishments - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/assignedfe/`)
        .then(response => response.data.filter(item => 
          (item.name_of_ca && item.name_of_ca.toLowerCase().includes(lowerQuery)) ||
          (item.designation && item.designation.toLowerCase().includes(lowerQuery)) ||
          (item.name_of_fe && item.name_of_fe.toLowerCase().includes(lowerQuery)) ||
          (item.ph_number && item.ph_number.toLowerCase().includes(lowerQuery)) ||
          (item.email && item.email.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: `${item.name_of_ca} - ${item.designation}`,
          path: '/ca/authority/assingnedfe',
          category: 'Assigned Financial Establishments',
          description: `FE: ${item.name_of_fe} • Phone: ${item.ph_number || 'N/A'} • Email: ${item.email || 'N/A'}`,
          link: item.link,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['name_of_ca', 'designation', 'name_of_fe', 'ph_number', 'email'])
        })))
        .catch(() => [])
    );

    // 4. Calendar Events
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/calender-event/`)
        .then(response => response.data.filter(item => 
          (item.event_name && item.event_name.toLowerCase().includes(lowerQuery)) ||
          (item.start_date && item.start_date.toLowerCase().includes(lowerQuery)) ||
          (item.end_date && item.end_date.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.event_name,
          path: '/about/calenderEvent',
          category: 'Calendar Events',
          description: `Start: ${item.start_date} • End: ${item.end_date}`,
          link: item.link,
          date: item.start_date,
          relevance: calculateRelevance(item, lowerQuery, ['event_name', 'start_date', 'end_date'])
        })))
        .catch(() => [])
    );

    // 5. Head Office Contacts - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/dep-head-offices/`)
        .then(response => response.data.filter(item => 
          (item.designation && item.designation.toLowerCase().includes(lowerQuery)) ||
          (item.ph_number && item.ph_number.toLowerCase().includes(lowerQuery)) ||
          (item.email && item.email.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.designation,
          path: '/contact/departmentcontacts',
          category: 'Department Contacts',
          description: `Phone: ${item.ph_number || 'N/A'} • Email: ${item.email || 'N/A'}`,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['designation', 'ph_number', 'email'])
        })))
        .catch(() => [])
    );

    // 6. KPID Act
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/kpid-act/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal/law/kpid-act',
          category: 'KPID Act',
          description: `KPID Act - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 7. BUDS Act
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/budsact/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal/law/buds-act',
          category: 'BUDS Act',
          description: `BUDS Act - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 8. Daily Court Cases - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/daily-court-orders/`)
        .then(response => response.data.filter(item => 
          (item.case_no && item.case_no.toLowerCase().includes(lowerQuery)) ||
          (item.kpid_sec && item.kpid_sec.toLowerCase().includes(lowerQuery)) ||
          (item.petitioner && item.petitioner.toLowerCase().includes(lowerQuery)) ||
          (item.respondants && item.respondants.toLowerCase().includes(lowerQuery)) ||
          (item.status && item.status.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: `Case: ${item.case_no}`,
          path: '/legal/court-cases/daily-report',
          category: 'Court Cases',
          description: `Petitioner: ${item.petitioner || 'N/A'} • Respondent: ${item.respondants || 'N/A'} • Status: ${item.status || 'N/A'} • KPID Section: ${item.kpid_sec || 'N/A'}`,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['case_no', 'petitioner', 'respondants', 'status', 'kpid_sec'])
        })))
        .catch(() => [])
    );

    // 9. Key Judgements - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/key-judgement/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.order_no && item.order_no.toLowerCase().includes(lowerQuery)) ||
          (item.order_date && item.order_date.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal/court-cases/key-judgements',
          category: 'Key Judgements',
          description: `Order No: ${item.order_no || 'N/A'} • Date: ${item.order_date || 'N/A'} • Language: ${item.language || 'N/A'}`,
          link: item.link,
          date: item.order_date,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'order_no', 'order_date', 'language'])
        })))
        .catch(() => [])
    );

    // 10. Circular Notifications
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/circular/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/circulars',
          category: 'Circulars & Notifications',
          description: `Circular - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 11. Tenders
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/tender/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/public-notices/tender',
          category: 'Tenders',
          description: `Tender - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 12. Auctions
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/auction/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/public-notices/auction',
          category: 'Auctions',
          description: `Auction - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 13. Advertisements
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/advatisements/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/public-notices/adv',
          category: 'Advertisements',
          description: `Advertisement - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 14. Depositor Information
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/depositor-info/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/public-notices/ice/info-to-depositors',
          category: 'Depositor Information',
          description: `Depositor Info - ${item.language || ''}`,
          link: item.link,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'language'])
        })))
        .catch(() => [])
    );

    // 15. Office Orders
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/office-orders/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/sploca-circular/office-orders',
          category: 'Office Orders',
          description: `Office Order | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue'])
        })))
        .catch(() => [])
    );

    // 16. Manuals & Videos
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/manaual-vid/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/notification/public-notices/ice/manuals-videos',
          category: 'Manuals & Videos',
          description: 'Manual/Video Resource',
          link: item.link,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['subject'])
        })))
        .catch(() => [])
    );

    // 17. Daily High Court Cases - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/daily-high-court-cases/`)
        .then(response => response.data.filter(item => 
          (item.case_no && item.case_no.toLowerCase().includes(lowerQuery)) ||
          (item.name_of_the_parties && item.name_of_the_parties.toLowerCase().includes(lowerQuery)) ||
          (item.fe && item.fe.toLowerCase().includes(lowerQuery)) ||
          (item.status && item.status.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_filling && item.date_of_filling.toLowerCase().includes(lowerQuery)) ||
          (item.previous_date_of_hearing && item.previous_date_of_hearing.toLowerCase().includes(lowerQuery)) ||
          (item.next_date_of_hearing && item.next_date_of_hearing.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: `High Court Case: ${item.case_no}`,
          path: '/daily-high-court-cases',
          category: 'High Court Cases',
          description: `Parties: ${item.name_of_the_parties || 'N/A'} • FE: ${item.fe || 'N/A'} • Status: ${item.status || 'N/A'} • Filing Date: ${item.date_of_filling || 'N/A'}`,
          date: item.date_of_filling,
          relevance: calculateRelevance(item, lowerQuery, ['case_no', 'name_of_the_parties', 'fe', 'status', 'date_of_filling', 'previous_date_of_hearing', 'next_date_of_hearing'])
        })))
        .catch(() => [])
    );

    // 18. Daily Supreme Court Cases - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/daily-supreme-court-cases/`)
        .then(response => response.data.filter(item => 
          (item.case_no && item.case_no.toLowerCase().includes(lowerQuery)) ||
          (item.name_of_the_parties && item.name_of_the_parties.toLowerCase().includes(lowerQuery)) ||
          (item.status && item.status.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_filling && item.date_of_filling.toLowerCase().includes(lowerQuery)) ||
          (item.previous_date_of_hearing && item.previous_date_of_hearing.toLowerCase().includes(lowerQuery)) ||
          (item.next_date_of_hearing && item.next_date_of_hearing.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: `Supreme Court Case: ${item.case_no}`,
          path: '/daily-supreme-court-cases',
          category: 'Supreme Court Cases',
          description: `Parties: ${item.name_of_the_parties || 'N/A'} • Status: ${item.status || 'N/A'} • Filing Date: ${item.date_of_filling || 'N/A'}`,
          date: item.date_of_filling,
          relevance: calculateRelevance(item, lowerQuery, ['case_no', 'name_of_the_parties', 'status', 'date_of_filling', 'previous_date_of_hearing', 'next_date_of_hearing'])
        })))
        .catch(() => [])
    );

    // 19. Other State PIDFE
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/other-state-pidfe/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/other-state-pidfe',
          category: 'Other State PIDFE',
          description: `Other State PIDFE - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 20. Other Relevant Acts
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/other-relevent-acts/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/other-relevent-acts',
          category: 'Other Relevant Acts',
          description: `Other Act - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 21. Comparison of Acts
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/comparisn-of-acts/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/comparisn-of-acts',
          category: 'Comparison of Acts',
          description: `Act Comparison - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 22. FAQs
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/faq/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/faq',
          category: 'FAQs',
          description: `FAQ - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 23. Legal Blogs/Queries
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/legal-blog/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal-blogs',
          category: 'Legal Queries',
          description: `Legal Query - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 24. Scam News
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/scam-news/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/scam-news',
          category: 'Scam News',
          description: `Scam News - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 25. High Court Orders - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/high-court-orders/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.order_no && item.order_no.toLowerCase().includes(lowerQuery)) ||
          (item.order_date && item.order_date.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/high-court-orders',
          category: 'High Court Orders',
          description: `Order No: ${item.order_no || 'N/A'} • Date: ${item.order_date || 'N/A'} • Language: ${item.language || 'N/A'}`,
          link: item.link,
          date: item.order_date,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'order_no', 'order_date', 'language'])
        })))
        .catch(() => [])
    );

    // 26. Supreme Court Orders - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/supreme-court-orders/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.order_no && item.order_no.toLowerCase().includes(lowerQuery)) ||
          (item.order_date && item.order_date.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/supreme-court-orders',
          category: 'Supreme Court Orders',
          description: `Order No: ${item.order_no || 'N/A'} • Date: ${item.order_date || 'N/A'} • Language: ${item.language || 'N/A'}`,
          link: item.link,
          date: item.order_date,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'order_no', 'order_date', 'language'])
        })))
        .catch(() => [])
    );

    // 27. Other State Court Orders - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/other-state-court-orders/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.order_no && item.order_no.toLowerCase().includes(lowerQuery)) ||
          (item.order_date && item.order_date.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/other-state-court-orders',
          category: 'Other State Court Orders',
          description: `Order No: ${item.order_no || 'N/A'} • Date: ${item.order_date || 'N/A'} • Language: ${item.language || 'N/A'}`,
          link: item.link,
          date: item.order_date,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'order_no', 'order_date', 'language'])
        })))
        .catch(() => [])
    );

    // 28. SOP
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/sop/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/sop',
          category: 'SOP',
          description: `SOP - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 29. Legal Opinion
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/legal-opinion/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal-opinion',
          category: 'Legal Opinions',
          description: `Legal Opinion - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 30. Legal Research
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/legal-research/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.date_of_issue && item.date_of_issue.toLowerCase().includes(lowerQuery)) ||
          (item.language && item.language.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/legal-research',
          category: 'Legal Research',
          description: `Legal Research - ${item.language || ''} | Date: ${item.date_of_issue || ''}`,
          link: item.link,
          date: item.date_of_issue,
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'date_of_issue', 'language'])
        })))
        .catch(() => [])
    );

    // 31. Legal Team - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/legal-team/`)
        .then(response => response.data.filter(item => 
          (item.name && item.name.toLowerCase().includes(lowerQuery)) ||
          (item.designation && item.designation.toLowerCase().includes(lowerQuery)) 
          // (item.roles_and_responsibility && item.roles_and_responsibility.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.name,
          path: '/legal/law/legal-team',
          category: 'Employee Directory',
          description: `Designation: ${item.designation || 'N/A'}}`,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['name', 'designation'])
        })))
        .catch(() => [])
    );

    // 32. News - UPDATED with all fields
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/news/`)
        .then(response => response.data.filter(item => 
          (item.subject && item.subject.toLowerCase().includes(lowerQuery)) ||
          (item.description && item.description.toLowerCase().includes(lowerQuery))
        ).map(item => ({
          type: 'content',
          title: item.subject,
          path: '/',
          category: 'News',
          description: item.description || 'Latest News',
          link: item.link,
          date: '',
          relevance: calculateRelevance(item, lowerQuery, ['subject', 'description'])
        })))
        .catch(() => [])
    );

    // 33. Social News/Notifications
    searchPromises.push(
      axios.get(`${API_BASE_URL}/home-notification/social-news/`)
        .then(response => {
          const data = response.data;
          if (data && data.subject && data.subject.toLowerCase().includes(lowerQuery)) {
            return [{
              type: 'content',
              title: data.subject,
              path: '/',
              category: 'Notifications',
              description: 'Important Notification',
              link: data.link,
              date: '',
              relevance: calculateRelevance(data, lowerQuery, ['subject'])
            }];
          }
          return [];
        })
        .catch(() => [])
    );

    try {
      // Execute all search promises in parallel
      const results = await Promise.allSettled(searchPromises);
      
      // Combine all successful results
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          contentResults.push(...result.value);
        }
      });

    } catch (error) {
      console.error('Error in content search:', error);
    }

    return contentResults;
  };

  // Calculate relevance score for better sorting
  const calculateRelevance = (item, query, fields) => {
    let score = 0;
    const exactQuery = query.toLowerCase();
    
    fields.forEach(field => {
      if (item[field]) {
        const fieldValue = item[field].toLowerCase();
        if (fieldValue === exactQuery) {
          score += 10; // Exact match
        } else if (fieldValue.includes(exactQuery)) {
          score += 5; // Partial match
        }
        if (field === 'name' || field === 'title' || field === 'subject' || field === 'question') {
          score += 3; // Boost for important fields
        }
      }
    });
    
    return score;
  };

  const searchData = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      // Search routes (existing functionality)
      const routeResults = performRouteSearch(query, searchableRoutes);
      
      // Search content from various APIs
      const contentResults = await searchContent(query);
      
      // Combine all results and sort by relevance
      const allResults = [...routeResults, ...contentResults]
        .sort((a, b) => {
          // First, prioritize by type (routes first, then content)
          if (a.type === 'route' && b.type === 'content') return -1;
          if (a.type === 'content' && b.type === 'route') return 1;
          
          // Then sort by relevance score (highest first)
          const aScore = a.relevance || 0;
          const bScore = b.relevance || 0;
          if (bScore !== aScore) return bScore - aScore;
          
          // Finally, sort by title alphabetically
          return a.title.localeCompare(b.title);
        })
        .slice(0, 100); // Increased limit for comprehensive results

      setSearchResults(allResults);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to route-only search
      const routeResults = performRouteSearch(query, searchableRoutes);
      setSearchResults(routeResults);
    } finally {
      setIsSearching(false);
    }
  };

  // Simple route search function
  const performRouteSearch = (query, routes) => {
    const lowerQuery = query.toLowerCase().trim();
    
    if (!lowerQuery) return [];

    const results = routes.filter(item => 
      item.name.toLowerCase().includes(lowerQuery) ||
      item.to.toLowerCase().includes(lowerQuery) ||
      item.category.toLowerCase().includes(lowerQuery)
    );
    
    return results.map(item => ({
      type: 'route',
      title: item.name,
      path: item.to,
      category: item.category,
      description: `Navigate to ${item.name}`,
      external: item.external,
      relevance: calculateRelevance(item, lowerQuery, ['name', 'category'])
    }));
  };

  // Clear search results
  const clearResults = () => {
    setSearchResults([]);
  };

  const value = {
    searchResults,
    isSearching,
    searchData,
    clearResults
  };

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
};

export const useGlobalSearch = () => {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error('useGlobalSearch must be used within GlobalSearchProvider');
  }
  return context;
};